import { useSelector } from 'react-redux'
import { useMemo } from 'react'
import { Grid, SelectChangeEvent } from '@mui/material'
import DropdownMenu from '../../../shared/UI/DropdownMenu'
import { Employee, LocationGroups, SelectOption } from '../../../services/dashboardService'
import CovidIsoPayAlert from '../../Absence/Actions/CovidIsoPayAlert'
import { RootStore } from '../../../redux/store'
import { SwitchContainer } from '../components'
import CheckBox from '../../../shared/UI/CheckBox'

interface AbsenceTypeSelectionProps {
  absenceReasons: SelectOption[]
  setAbsenceReason: (value: SelectOption) => void
  selectedAbsenceReason?: SelectOption
  absenceTypes: SelectOption[]
  locationAbsenceTypes: SelectOption[]
  setAbsenceType: (value: SelectOption | undefined) => void
  selectedAbsenceType?: SelectOption
  disabled: boolean
  covidIsolationIndicator: boolean | undefined
  setCovidIsolationIndicator: (value: boolean | undefined) => void
  selectedEmployee?: Employee
  absenceTypeError?: boolean
  absenceReasonError?: boolean
  isWfmAbsence?: boolean
}

function AbsenceTypeSelection({
  absenceReasons,
  setAbsenceReason,
  selectedAbsenceReason,
  absenceTypes,
  locationAbsenceTypes,
  setAbsenceType,
  selectedAbsenceType,
  disabled,
  covidIsolationIndicator,
  setCovidIsolationIndicator,
  selectedEmployee,
  absenceTypeError = false,
  absenceReasonError = false,
  isWfmAbsence = false,
}: AbsenceTypeSelectionProps) {
  const { territoryAttendanceUK } = useSelector((state: RootStore) => state.featureToggles)

  const sickness = useMemo(
    () => absenceReasons.find(x => x.value === 1),
    [absenceReasons]
  )
  
  const notRequiredNotDisclosed = useMemo(
    () => absenceTypes.find(x => x.value === 25),
    [absenceTypes]
  )
  
  const shouldUseEmployeeReasons = useMemo(
    () => territoryAttendanceUK && selectedEmployee?.locationGroupName === LocationGroups.UK,
    [selectedEmployee?.locationGroupName, territoryAttendanceUK]
  )
  
  const processWithUkTaFlow = useMemo(
    () => territoryAttendanceUK && selectedEmployee?.locationGroupName === LocationGroups.UK,
    [territoryAttendanceUK, selectedEmployee?.locationGroupName]
  )
  
  const absenceReasonsForEmployee = () =>
    (shouldUseEmployeeReasons ? locationAbsenceTypes : absenceTypes)
      .filter(absenceType => absenceType.associatedValue === selectedAbsenceReason?.value
  )

  const additionalBehaviourOnTypeChange = (selected: SelectOption) => {
    setAbsenceType(undefined)
    
    if (processWithUkTaFlow) {
      if (selected?.value === sickness?.value) {
        setCovidIsolationIndicator(false)
        setAbsenceType(notRequiredNotDisclosed)
      } else {
        setCovidIsolationIndicator(undefined)
      }
      
      return
    }

    if (isWfmAbsence && selected?.value === sickness?.value) {
      setAbsenceType(notRequiredNotDisclosed)
    }
  }
  
  const onAbsenceTypeChange = (event: SelectChangeEvent) => {
    const selected = absenceReasons
      .find(item => `${item.value}` === `${event.target.value}`)

    if (!selected) {
      return
    }

    setAbsenceReason(selected)
    additionalBehaviourOnTypeChange(selected)
  }
  
  const absenceTypeSelection = (): JSX.Element => (
    <Grid item xs={12} md={6}>
      <DropdownMenu
        label="Absence Type"
        id="absenceType"
        data={absenceReasons}
        textField="displayValue"
        valueField="value"
        value={selectedAbsenceReason?.value ?? 0}
        onChange={onAbsenceTypeChange}
        error={absenceTypeError}
        helperText={!selectedAbsenceReason ? 'Required' : ''}
        disabled={disabled}
        dataTestId="MyActions-Absence-AbsenceType"
      />
    </Grid>
  )
  
  const onAbsenceReasonChange = (event: SelectChangeEvent) => {
    const selected = absenceTypes
      .find(item => `${item.value}` === `${event.target.value}`)

    if (!selected) {
      return
    }

    setAbsenceType(selected)
  }

  const absenceReasonSelection = (): JSX.Element => (
    <Grid item xs={12} md={6}>
      <DropdownMenu
        label="Reason for absence"
        id="absenceReason"
        data={absenceReasonsForEmployee()}
        textField="displayValue"
        valueField="value"
        value={selectedAbsenceType?.value ?? 0}
        onChange={onAbsenceReasonChange}
        error={absenceReasonError}
        helperText={!selectedAbsenceType ? 'Required' : ''}
        disabled={disabled || selectedAbsenceReason === undefined}
        dataTestId="MyActions-Absence-ReasonAbsence"
      />
    </Grid>
  )

  const covidIndicator = (): JSX.Element => (
    <Grid item xs={12} md={12}>
      <SwitchContainer>
        <CheckBox
          defaultChecked={false}
          label="Covid Indicator"
          name="covidIsolationIndicator"
          onChange={event => {
            const isCovid = event.target.checked
            const nextReason = isCovid
              ? absenceTypes.find(x => x.value === 22)
              : absenceTypes.find(x => x.value === 25)
            setAbsenceType(nextReason)
            setCovidIsolationIndicator(isCovid)
          }}
        />
      </SwitchContainer>
    </Grid>
  )
  

  const UKTAReasonSelector = () => (
    (selectedAbsenceReason?.value ?? 0) !== 1
      ? absenceReasonSelection()
      : covidIndicator()
  )

  const absenceSelectionComponent = (): JSX.Element => {
    if (processWithUkTaFlow) {
      return (
        <>
          {absenceTypeSelection()}
          {selectedAbsenceReason ? UKTAReasonSelector() : null}
        </>
      )
    }

    return (
      <>
        {absenceTypeSelection()}
        {absenceReasonSelection()}
      </>
    )
  }

  return (
    <>
      {absenceSelectionComponent()}
      <CovidIsoPayAlert
        absenceCategoryId={selectedAbsenceReason?.value ?? 0}
        absenceTypeId={selectedAbsenceType?.value ?? 0}
        covidIsolationIndicator={covidIsolationIndicator}
      />
    </>
  )
}

export default AbsenceTypeSelection
